import {
    Component
} from "@fingerprintjs/fingerprintjs";

export interface SensorFlags {
    hasOrientationApi: boolean,
    hasAccelerometerApi: boolean,
    hasOrientationChanged: boolean,
    hasAccelerationChanged: boolean
}

interface GyroscopeReadings {
    absolute: boolean,
    alpha: number | null,
    beta: number | null,
    gamma: number | null
}

interface AccelerationData {
    x: number | null,
    y: number | null,
    z: number | null
}

interface RotationRate {
    alpha: number | null,
    beta: number | null,
    gamma: number | null
}

interface AccelerometerReadings {
    acceleration: AccelerationData | null,
    accelerationIncludingGravity: AccelerationData | null,
    interval: number | null,
    rotationRate: RotationRate | null
}

const SENSORS_TIMEOUT = 300;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function checkDataChanged(o1: any, o2: any): boolean {
    return JSON.stringify(o1) !== JSON.stringify(o2);
}

interface GyroscopeData {
    hasOrientationChanged? : boolean;
    readings? : GyroscopeReadings | null;
    error? : unknown;
    hasOrientationApi: boolean;
}

interface AccelerometerData {
    hasAccelerationChanged? : boolean;
    readings? : AccelerometerReadings | null;
    error? : unknown;
    hasAccelerometerApi: boolean;
}

// getGyroscopeData - Waits for the deviceorientation event to be triggered
async function getGyroscopeData(timeoutMs: number): Promise < GyroscopeData > {
    return new Promise((resolve) => {
        if (window.DeviceOrientationEvent) {
            const timeout = setTimeout(() => {
                window.removeEventListener("deviceorientation", orientationHandler);
                resolve({
                    hasOrientationApi: true,
                    error: "Gyroscope event took too long"
                });
            }, timeoutMs);

            const startTime = Date.now();
            let eventCount = 0;
            let initialReading: GyroscopeReadings;
            const orientationHandler = (e: DeviceOrientationEvent) => {
                eventCount++;
                if (eventCount > 1) {
                    // Wait till half the timeout to make sure sensor has time to change
                    if (Date.now() - startTime < timeoutMs / 2) {
                        return;
                    }
                    clearTimeout(timeout);
                    window.removeEventListener("deviceorientation", orientationHandler);
                    const secondReading = {
                        absolute: e.absolute,
                        alpha: e.alpha,
                        beta: e.beta,
                        gamma: e.gamma
                    };
                    resolve({
                        hasOrientationApi: true,
                        hasOrientationChanged: checkDataChanged(initialReading, secondReading),
                        readings: secondReading
                    });
                } else {
                    initialReading = {
                        absolute: e.absolute,
                        alpha: e.alpha,
                        beta: e.beta,
                        gamma: e.gamma
                    };
                }
            };
            window.addEventListener("deviceorientation", orientationHandler, true);
        } else {
            resolve({
                hasOrientationApi: false,
                error: "DeviceOrientationEvent not supported"
            });
        }
    });
}

// getAccelerometer - Waits for the accelerometer event to be fired or until timeout
async function getAccelerometer(timeoutMs: number): Promise < AccelerometerData > {
    return new Promise((resolve) => {
        if (window.DeviceMotionEvent) {
            const timeout = setTimeout(() => {
                window.removeEventListener("devicemotion", deviceMotionHandler, true);
                resolve({
                    hasAccelerometerApi: true,
                    error: "Accelerometer event took too long"
                });
            }, timeoutMs);

            const startTime = Date.now();
            let eventCount = 0;
            let initialReading: AccelerometerReadings;
            const deviceMotionHandler = (e: DeviceMotionEvent) => {
                eventCount++;
                let acceleration = null;
                let accelerationIncludingGravity = null;
                let rotationRate = null;
                if (e.acceleration) {
                    acceleration = {
                        x: e.acceleration.x,
                        y: e.acceleration.y,
                        z: e.acceleration.z
                    };
                }
                if (e.accelerationIncludingGravity) {
                    accelerationIncludingGravity = {
                        x: e.accelerationIncludingGravity.x,
                        y: e.accelerationIncludingGravity.y,
                        z: e.accelerationIncludingGravity.z
                    };
                }
                if (e.rotationRate) {
                    rotationRate = {
                        alpha: e.rotationRate.alpha,
                        beta: e.rotationRate.beta,
                        gamma: e.rotationRate.gamma
                    };
                }
                if (eventCount > 1) {
                    // Wait till half the timeout to make sure sensor has time to change
                    if (Date.now() - startTime < timeoutMs / 2) {
                        return;
                    }
                    clearTimeout(timeout);
                    window.removeEventListener("devicemotion", deviceMotionHandler, true);

                    const secondReading: AccelerometerReadings = {
                        acceleration,
                        accelerationIncludingGravity,
                        interval: e.interval,
                        rotationRate
                    };

                    // interval property is always changing, only need to check acceleration values
                    const removeProp = "interval";
                    const {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        [removeProp]: tmp1, ...o1
                    } = initialReading;
                    const {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        [removeProp]: tmp2, ...o2
                    } = secondReading;

                    resolve({
                        hasAccelerometerApi: true,
                        hasAccelerationChanged: checkDataChanged(o1, o2),
                        readings: secondReading
                    });
                } else {
                    initialReading = {
                        acceleration,
                        accelerationIncludingGravity,
                        interval: e.interval,
                        rotationRate
                    };
                }
            };
            window.addEventListener("devicemotion", deviceMotionHandler, true);
        } else {
            resolve({
                hasAccelerometerApi: false,
                error: "DeviceMotionEvent not supported"
            });
        }
    });
}

// GetSensorsReadings - Implements the interface GetCustomComponent to get sensor readings
export async function GetSensorReadings(): Promise < Component < unknown >> {
    const error: Error | undefined = undefined;
    const startTime = Date.now();
    const result = await Promise.all([
        getGyroscopeData(SENSORS_TIMEOUT),
        getAccelerometer(SENSORS_TIMEOUT)
    ]);

    const duration = Date.now() - startTime;

    if (result) {
        const gyroscopeData = result[0] as GyroscopeData;
        const accelerometerData = result[1] as AccelerometerData;

        const sensorFlags: SensorFlags = {
            hasOrientationApi: gyroscopeData.hasOrientationApi,
            hasAccelerometerApi: accelerometerData.hasAccelerometerApi,
            hasOrientationChanged: gyroscopeData.hasOrientationChanged ? gyroscopeData.hasOrientationChanged : false,
            hasAccelerationChanged: accelerometerData.hasAccelerationChanged ? accelerometerData.hasAccelerationChanged : false,
        };

        return {
            value: sensorFlags,
            duration
        };
    } else {
        return {
            duration: duration,
            error: error ? error : new Error("Unknown error"),
        };
    }
}
